<template>
	<div ref="timelineRef" class="timeline">
		<div class="timeline__animation">
			<img
				src="/images/pages/publishers/advantages/ball.png"
				alt="ball"
				loading="lazy"
				decoding="async"
				class="timeline__animation-ball" />
			<img
				src="/images/pages/publishers/advantages/ball.png"
				alt="ball"
				loading="lazy"
				decoding="async"
				class="timeline__animation-ball" />
			<img
				src="/images/pages/publishers/advantages/ball.png"
				alt="ball"
				loading="lazy"
				decoding="async"
				class="timeline__animation-ball" />
			<img
				src="/images/pages/publishers/advantages/ball.png"
				alt="ball"
				loading="lazy"
				decoding="async"
				class="timeline__animation-ball" />
			<img
				src="/images/pages/publishers/advantages/ball.png"
				alt="ball"
				loading="lazy"
				decoding="async"
				class="timeline__animation-ball" />
			<img
				src="/images/pages/publishers/advantages/ball.png"
				alt="ball"
				loading="lazy"
				decoding="async"
				class="timeline__animation-ball" />
			<img
				src="/images/pages/publishers/advantages/image.png"
				alt="timeline"
				loading="lazy"
				decoding="async"
				class="timeline__animation-image" />
		</div>
		<div class="timeline__content">
			<BlocksPublishersAdvantagesAccordionItem
				v-for="(item, i) in data"
				:key="i"
				class="timeline__content-item">
				<template #title>
					<Icon :name="$rt(item.icon)" size="24" />
					<UIHeading level="5" class="timeline__content-title">
						{{ $rt(item.title) }}
					</UIHeading>
				</template>
				<template v-if="item.text || item.list" #content>
					<p  class="timeline__content-text">
						{{ $rt(item.text || '') }}
					</p>
					<ul v-if="item.list" class="timeline__content-list">
						<li v-for="(listItem, idx) in item.list" :key="idx">
							{{ $rt(listItem) }}
						</li>
					</ul>
				</template>
			</BlocksPublishersAdvantagesAccordionItem>
		</div>
	</div>
</template>
<script setup lang="ts">
	const timelineRef = ref<HTMLElement | null>(null)
	const { $gsap: gsap } = useNuxtApp()
	let ballsTl: any = null
	let accordionItemsTl: any = null
	let animationEl: HTMLElement | null = null
	let ballsEls: HTMLElement[] = []
	let onMouseMove: ((event: MouseEvent) => void) | null = null
	let onMouseLeave: (() => void) | null = null

	defineProps<{
		data: { icon: string; title: string; text?: string; list?: string[] }[]
	}>()

	onMounted(() => {
		const timelineEl = timelineRef.value
		if (!timelineEl) return
		const isMobile = window.matchMedia('(max-width: 991.98px)').matches
		const scrubValue = isMobile ? false : 1

		animationEl = timelineEl.querySelector<HTMLElement>('.timeline__animation')
		if (!animationEl) return

		const balls = timelineEl.querySelectorAll<HTMLElement>('.timeline__animation-ball')
		if (!balls.length) return
		ballsEls = Array.from(balls)

		const accordionItems = timelineEl.querySelectorAll<HTMLElement>('.timeline__content-item')
		if (!accordionItems.length) return

		gsap.set(balls, {
			yPercent: 20,
			autoAlpha: 0,
		})

		ballsTl = gsap.timeline({
			scrollTrigger: {
				trigger: timelineEl,
				start: 'top bottom',
				end: 'bottom bottom',
				scrub: scrubValue,
			},
		})

		accordionItemsTl = gsap.timeline({
			scrollTrigger: {
				trigger: timelineEl,
				start: 'top bottom',
				end: 'bottom bottom',
				scrub: true,
			},
		})

		accordionItemsTl.from(accordionItems, {
			opacity: 0,
			stagger: 0.35,
			yPercent: 20,
			ease: 'none',
		})

		ballsTl.to(balls, {
			yPercent: -200,
			autoAlpha: 1,
			stagger: 0.15,
			ease: 'none',
		})

		if (!isMobile) {
			const moveStrength = 24
			onMouseMove = (event: MouseEvent) => {
				if (!animationEl || !ballsEls.length) return

				const bounds = animationEl.getBoundingClientRect()
				const centerX = bounds.left + bounds.width / 2
				const centerY = bounds.top + bounds.height / 2
				const normalizedX = (event.clientX - centerX) / (bounds.width / 2 || 1)
				const normalizedY = (event.clientY - centerY) / (bounds.height / 2 || 1)

				ballsEls.forEach((ball, index) => {
					const depth = 0.45 + index * 0.1
					gsap.to(ball, {
						x: normalizedX * moveStrength * depth,
						y: normalizedY * moveStrength * depth,
						duration: 0.35,
						ease: 'power2.out',
						overwrite: 'auto',
					})
				})
			}

			onMouseLeave = () => {
				if (!ballsEls.length) return
				gsap.to(ballsEls, {
					x: 0,
					y: 0,
					duration: 0.5,
					ease: 'power2.out',
					overwrite: 'auto',
				})
			}

			animationEl.addEventListener('mousemove', onMouseMove)
			animationEl.addEventListener('mouseleave', onMouseLeave)
		}
	})

	onBeforeUnmount(() => {
		ballsTl?.scrollTrigger?.kill()
		ballsTl?.kill()
		ballsTl = null
		accordionItemsTl?.scrollTrigger?.kill()
		accordionItemsTl?.kill()
		accordionItemsTl = null

		if (animationEl && onMouseMove) {
			animationEl.removeEventListener('mousemove', onMouseMove)
		}
		if (animationEl && onMouseLeave) {
			animationEl.removeEventListener('mouseleave', onMouseLeave)
		}

		animationEl = null
		ballsEls = []
		onMouseMove = null
		onMouseLeave = null
	})
</script>
<style lang="scss" scoped>
	.timeline {
		display: flex;
		align-items: flex-start;

		@media (width <= 991.98px) {
			flex-direction: column;
			align-items: center;
		}
	}
	.timeline__animation {
		position: sticky;
		top: 90px;
		flex: 0 0 40%;

		@media (width <= 991.98px) {
			position: relative;
			top: 0;
			flex: 0 0 100%;
		}
		&-image {
			z-index: -1;
			width: 100%;
		}
		&-ball {
			position: absolute;
			top: 29%;
			left: 48%;
			z-index: 1;
			width: 8%;
			&:nth-child(1) {
				top: 37%;
				left: 64%;
			}
			&:nth-child(2) {
				top: 29%;
				left: 48%;
			}
			&:nth-child(3) {
				top: 16%;
				left: 35%;
			}
			&:nth-child(4) {
				top: 16%;
				left: 57%;
			}
			&:nth-child(5) {
				top: 23%;
				left: 42%;
			}
			&:nth-child(6) {
				top: 23%;
				left: 22%;
			}
		}
	}
	.timeline__content {
		display: flex;
		flex: 1 1 60%;
		flex-direction: column;
		gap: 32px;
        &-text{
            margin-bottom: 16px;
        }
		&-list {
			padding-left: 1em;
			list-style: disc;

			// text-indent: -1.5em;
		}
	}
</style>
